import { ICreateShop, ICreateShopObject, ICreateShopAction } from 'models/shop/createshop';
import { IStore } from 'redux/IStore';
import { ICreateShopProfile, IShopCreateDto, IShopProfile } from 'models/shop/shopprofile';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
import { destroy, change } from 'redux-form';
import * as c from 'models/constants';
const { notifSend } = notifActions;

import { getMyUserPreferences } from 'redux/modules/user/userpreferences';
import { ShopStatus } from 'models';
import { addAdmins, addOffices, addImageToShop } from '../helpers';
import { ICropImageData, IImageData } from 'models/common';
import { CountryAlpha2CodeToCurrency } from 'helpers/countryAlpha2CodeToCurrency';

/** Action Types */
export const REQUEST_START: string = 'createshop/REQUEST_START';
export const REQUEST_FAILURE: string = 'createshop/REQUEST_FAILURE';
export const CREATE_SHOP_SUCCESS: string = 'createshop/CREATE_SHOP_SUCCESS';
export const CREATE_SHOP_CROPIMAGE_SUCCESS: string = 'createshop/CREATE_SHOP_CROPIMAGE_SUCCESS';
export const CLEAR_FORM_DATA: string = 'createshop/CLEAR_FORM_DATA';
export const NEXT_STEP: string = 'createshop/NEXT_STEP';
export const SET_STEP: string = 'createshop/SET_STEP';
export const PREVIOUS_STEP: string = 'createshop/PREVIOUS_STEP';
export const SET_CUSTOM_SHIPPING: string = 'createshop/SET_CUSTOM_SHIPPING';

/** Initial State */
const initialState: ICreateShop = {
	createShopObj: {
		shippingOption: {
			freeShipping: false,
		},
		shop: {
			currencyKey: null, // dont fallback!
			status: ShopStatus.Draft,
			admins: [],
		},
	},
	wizardStep: 0,
};

/** Reducer */
export function createShopReducer(state = initialState, action: ICreateShopAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case CREATE_SHOP_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				error: false,
				createShopObj: action.payload.createShopObj,
			});

		case CREATE_SHOP_CROPIMAGE_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
			});

		case CLEAR_FORM_DATA:
			return Object.assign({}, state, {
				error: false,
				createShopObj: {
					shippingOption: {
						freeShipping: false,
					},
				},
				wizardStep: 0,
			});

		case SET_STEP:
			return Object.assign({}, state, {
				wizardStep: action.payload.wizardStep || 0,
			});

		case NEXT_STEP:
			return Object.assign({}, state, {
				wizardStep: state.wizardStep + 1,
			});

		case PREVIOUS_STEP:
			return Object.assign({}, state, {
				wizardStep: Math.max(0, state.wizardStep - 1),
			});

		case SET_CUSTOM_SHIPPING:
			return Object.assign({}, state, {
				error: false,
				createShopObj: Object.assign({}, state.createShopObj, {
					customShipping: Boolean(action.payload.createShopObj.customShipping),
				}),
			});

		default:
			return state;
	}
}

/** Action Creator */
export function submitCreateShop(createShopObj: ICreateShopObject) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		if (createShopObj.shop.socialLinksObj) {
			createShopObj.shop.socialLinks = JSON.stringify(createShopObj.shop.socialLinksObj);
		}

		createShopObj.shop.ownerId = getState().userPreferences.id;
		// createShopObj.shop.status = createShopObj.shop.isActive ? ShopStatus.InApproval : ShopStatus.Draft;

		const copy: ICreateShopProfile = JSON.parse(JSON.stringify(createShopObj.shop));

		// Added outer if statement otherwise it would not let you through the create shop form without adding a shop profile picture
		if (copy.imageCrop) {
			if (!copy.imageCrop.createThumbnail && !copy.imageCrop.thumbnails) {
				copy.imageCrop.createThumbnail = true;
				copy.imageCrop.thumbnails = c.DEFAULT_CROP_SIZES_PROFILE_IMAGE;
			}

			if (!copy.imageUrl && !copy.imageId) {
				copy.imageUrl = createShopObj?.picture;
				copy.imageId = createShopObj?.pictureId;
			}
		}

		const offices = copy.offices || [];
		const mainOffice = offices.find((o) => o.isDefault);
		if (!mainOffice) {
			return dispatch(requestFailure('Main location missing'));
		}

		const countryDomainValues = getState().domainValues.countries;
		const currencyDomainValues = getState().domainValues.currencies;

		const countryAlpha2 = countryDomainValues.find((element) => element.key === copy.fromCountryKey)?.alpha2Code;
		const currency = CountryAlpha2CodeToCurrency[countryAlpha2];
		const currencyKey = currencyDomainValues.find((element) => element.value === currency)?.key;

		const body: IShopCreateDto = {
			id: copy.id,
			ownerId: copy.ownerId,
			code: copy.code,
			name: copy.name,
			description: copy.description || copy.name,
			isActive: copy.isActive,
			location: mainOffice.location,
			email: copy.shippingEmail,
			callingCodeKey: mainOffice.callingCodeKey,
			phoneNumber: mainOffice.phoneNumber,
			about: copy.about,
			website: copy.website,
			tags: copy.tags,
			status: copy.status,
			metatagTitle: copy.metatagTitle,
			currencyKey,
			shopTypes: copy.shopTypes,
			// socialLinks: copy.ownerId,
			// votes: copy.ownerId,
			// totalRating: copy.ownerId,
			// averageRating: copy.ownerId,
			// isTrustedSeller: copy.ownerId,
			geoLocationLatitude: mainOffice.geoLocationLatitude,
			geoLocationLongitude: mainOffice.geoLocationLongitude,

			shippingAddress: mainOffice.address || mainOffice.location,
			shippingCity: mainOffice.city,
			shippingPostalCode: mainOffice.postalCode,
			shippingEmail: copy.shippingEmail,
			shippingPhone: mainOffice.fullPhone || mainOffice.phoneNumber,
			shippingFromCountryKey: copy.shippingFromCountryKey || copy.fromCountryKey,
			shippingName: copy.name,
			shippingProvince: mainOffice.province,
			isEmailNotificationEnabled: true,
			isSMSNotificationEnabled: false,
			isSendSMSNotificationEnabled: false,
			isOnlinePaymentEnabled: true,
			bookingInterval: 900, // default to 15 minutes for now
		};

		return fetch(
			getState().fetchDefault.baseAPI + 'api/shopprofile',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(body),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return (
						res
							.json()
							// .then(authorizeMyself(getState))
							.then((shop: IShopProfile) => {
								addImageToShop(getState, copy, shop);

								return Promise.allSettled([addAdmins(getState, copy, shop), addOffices(getState, copy, shop)]).then(
									() => {
										const proceed = () => {
											dispatch(
												notifSend({
													message: i18n.t('Notification_Success_Shop_Created'),
													kind: 'success',
													dismissAfter: 3000,
												}),
											);
											dispatch(
												createShopSuccess({
													...createShopObj,
													shop: {
														...createShopObj.shop,
														code: shop.code,
													},
												}),
											);
											dispatch(destroy(c.FORM_NAME_CREATE_SHOP));
											dispatch(setStep(4));
											dispatch(getMyUserPreferences()); // refetch permissions
										};
										if (!createShopObj.customShipping || !createShopObj.shippingOption) {
											return proceed();
										}
										createShopObj.shippingOption.shopId = shop.id;
										return fetch(
											getState().fetchDefault.baseAPI + 'api/Shop/ShippingOptions',
											Object.assign({}, getState().fetchDefault.requestInit, {
												method: 'POST',
												body: JSON.stringify(createShopObj.shippingOption),
											}),
										)
											.then((res) => {
												if (res.ok) {
													proceed();
												} else {
													const status: number = res.status;
													const statusText: string = res.statusText;
													return res
														.json()
														.then((res) => {
															res.status = status;
															res.statusText = statusText;
															dispatch(requestFailure(res));
														})
														.catch(() => {
															const message: any = { status, statusText };
															dispatch(requestFailure(message));
														});
												}
											})
											.catch((err) => {
												dispatch(requestFailure(err));
											});
									},
								);
							})
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator  */
// TODO: Currently this creates an extra cropped image only for the StepGeneral cropper to work
// Optimally the properties of this image would be used in the final step of create shop but
// currently we cannot do this as BE does not accept the image properties directly in '/api/ShopProfile' call
export function createShopCropImage(image: ICropImageData) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/ImageUpload/Crop',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(image),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newImage: IImageData) => {
						dispatch(createShopCropImageSuccess());
						dispatch(change(c.FORM_NAME_CREATE_SHOP, 'shop.extraImageUrl', newImage.href));
						dispatch(change(c.FORM_NAME_CREATE_SHOP, 'shop.extraImageId', newImage.id));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function requestStart(): ICreateShopAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): ICreateShopAction {
	console.warn(message);
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function clearFormData(): ICreateShopAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}

/** Action Creator */
export function createShopSuccess(createShopObj: ICreateShopObject): ICreateShopAction {
	return {
		type: CREATE_SHOP_SUCCESS,
		payload: {
			createShopObj,
		},
	};
}

/** Action Creator */
export function createShopCropImageSuccess(): ICreateShopAction {
	return {
		type: CREATE_SHOP_CROPIMAGE_SUCCESS,
	};
}

/** Action Creator */
export function nextStep(): ICreateShopAction {
	return {
		type: NEXT_STEP,
	};
}

/** Action Creator */
export function previousStep(): ICreateShopAction {
	return {
		type: PREVIOUS_STEP,
	};
}

/** Action Creator */
export function setStep(step?: number): ICreateShopAction {
	return {
		type: SET_STEP,
		payload: {
			wizardStep: step || 0,
		},
	};
}

/** Action Creator */
export function setCustomShipping(customShipping: boolean): ICreateShopAction {
	return {
		type: SET_CUSTOM_SHIPPING,
		payload: {
			createShopObj: {
				customShipping,
			},
		},
	};
}
